import React from 'react'
import flatpickr from 'flatpickr'
import styled, { css } from 'styled-components'

import { FieldMetadata, FieldProps, useFieldMetadata } from '../FieldMetadata'
import { Icon } from '../Icon'
import FormInput, { FormInputProps } from './FormInput'
import { flatPickerStyles } from './flatpickr'
import { CustomStyleOverrideProps } from './types'
import { getBrandPrimaryColor } from './utils'

const inputClassName = 'flatpickr-form-input'

type DatePickerProps = {
  inline?: boolean
  input?: boolean //if false, the input will not be rendered
  enable?: {
    to?: string | null
    from?: string | null
  }
  dateFormat?: 'F j, Y' | 'M d' | 'F j, Y H:i K'
  enableTime?: boolean
}

export type FormDateInputProps = (Omit<FieldProps, 'size' | 'type'> &
  DatePickerProps &
  Omit<FormInputProps, 'onChange'>) &
  (
    | {
        mode?: 'single'
        onChange?: (val?: string) => void
        value?: string | null
      }
    | {
        mode: 'range'
        onChange?: (val?: [string, string]) => void
        value?: [string, string] | null
      }
  )

const FormInputWrapper = styled.div`
  position: relative;
`

const ClearButton = styled.button<{ disabled?: boolean, baseOverrides?: Partial<CustomStyleOverrideProps> }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 15px;
  background: none;
  border: none;

  path {
    fill: ${(props) => getBrandPrimaryColor(props.baseOverrides)};
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}
`

const FormDateInput = ({
  mode,
  inline,
  input,
  errorMessage,
  value,
  onChange,
  disabled,
  clearable,
  enable,
  leftAdornment,
  rightAdornment,
  dateFormat,
  enableTime,
  ...props
}: FormDateInputProps) => {
  const { fieldProps, ...fieldMetadataProps } = useFieldMetadata({
    ...props,
    errorMessage,
  })
  const wrapperDivRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const flatpickrRef = React.useRef<flatpickr.Instance | null>(null)

  const defaultDates = React.useMemo(() => {
    if (mode === 'range') {
      if (Array.isArray(value)) {
        return [
          new Date(value[0] as string),
          new Date(value[1] as string) ?? new Date(value[0] as string),
        ]
      }

      return !!value ? [new Date(value as string)] : ''
    }

    return value ? new Date(value) : ''
  }, [mode, value])

  React.useEffect(() => {
    if (
      flatpickrRef.current &&
      typeof flatpickrRef.current.set === 'function'
    ) {
      flatpickrRef.current.set('clickOpens', !disabled)
    }
  }, [disabled, flatpickrRef.current])

  const handleChange = React.useCallback(
    (selectedDates: Date[] | undefined) => {
      if (mode === 'range') {
        onChange?.(
          selectedDates?.map((date) => date.toISOString()) ??
            ([].slice(0, 2) as any)
        )
      } else {
        onChange?.(selectedDates?.[0]?.toISOString() ?? '')
      }
    },
    [mode, onChange]
  )

  React.useEffect(() => {
    if (
      flatpickrRef.current &&
      typeof flatpickrRef.current.set === 'function'
    ) {
      flatpickrRef.current.set('onChange', handleChange)
    }
  }, [handleChange, flatpickrRef.current])

  React.useEffect(() => {
    if (
      !flatpickrRef.current ||
      typeof flatpickrRef.current.setDate !== 'function'
    )
      return
    // istanbul ignore next
    if (!defaultDates) return

    flatpickrRef.current.setDate(defaultDates)
  }, [defaultDates])

  React.useEffect(() => {
    if (!wrapperDivRef.current) return
    if (!inputRef.current) return

    const useRegularDateFormat = !enableTime && !dateFormat
    flatpickrRef.current = flatpickr(inputRef.current, {
      mode: mode ?? 'single',
      inline,
      disableMobile: true,
      appendTo: wrapperDivRef.current as HTMLElement,
      positionElement: inputRef.current,
      altInput: true,
      altFormat:
        dateFormat ?? (useRegularDateFormat ? 'F j, Y' : 'F j, Y H:i K'),
      altInputClass: inputClassName,
      dateFormat: useRegularDateFormat ? 'Y-m-d H:i' : 'Y-m-d H:i:S',
      enableTime,
      defaultDate: defaultDates,
      onChange: handleChange,
      onReady: (a, b, fp) => {
        fp.altInput!.setAttribute('aria-label', 'date picker input')
      },
    })

    if (!!enable && enable.to && enable.from) {
      if (
        flatpickrRef.current &&
        typeof flatpickrRef.current.set === 'function'
      ) {
        flatpickrRef.current.set('enable', [enable as any])
      }
    }

    return () => {
      if (flatpickrRef.current?.destroy) {
        flatpickrRef.current?.destroy()
      }

      flatpickrRef.current = null
    }
  }, [wrapperDivRef, inputRef])

  return (
    <FieldMetadata
      {...fieldMetadataProps}
      required={props.required}
      aria-label='date input'
      baseOverrides={props.baseOverrides}
    >
      <FormInputWrapper
        ref={wrapperDivRef}
        aria-label='date input'
        data-testid='form-date-input-testid'
        css={css`
          ${flatPickerStyles(props.baseOverrides)}

          .${inputClassName} {
            ${
              disabled &&
              `
            background-color: var(--gray100);
            cursor: not-allowed;
            color: var(--gray600);
            `
            }
          }
        `}
      >
        <FormInput
          leftAdornment={leftAdornment}
          rightAdornment={rightAdornment}
          ref={inputRef}
          aria-label='date form input'
          style={{ display: inline && input === false ? 'none' : 'block' }}
          onFocus={() => {
            // istanbul ignore next
            flatpickrRef.current?.open()
          }}
          baseOverrides={props.baseOverrides}
        />
        {clearable ? (
          <ClearButton
            disabled={Boolean(disabled)}
            baseOverrides={props.baseOverrides}
            onClick={(e) => {
              e.preventDefault()

              if (disabled) return

              flatpickrRef.current?.clear()

              const ev = new Event('input', { bubbles: true })
              inputRef?.current?.dispatchEvent(ev)
            }}
            aria-label='Clear Input'
            style={{
              cursor: 'pointer',
            }}
          >
            <Icon type='CLOSE_X' height={20} width={20} />
          </ClearButton>
        ) : null}
      </FormInputWrapper>
    </FieldMetadata>
  )
}
export default FormDateInput
